/// <reference types="vite/client" />
/**
 * /activity — cross-agent timeline of swarm_actions (issue #24).
 * Filters: agent, action substring, status, engagement, since.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorBanner } from "../components/ErrorBanner";
import { useEngagement } from "../hooks/useEngagement";

interface ActivityRow {
  id: string;
  timestamp: string;
  agent: string;
  action: string;
  status: string;
  engagementId: string;
  details: Record<string, unknown>;
}

const STATUS_OPTIONS: Array<{ key: string; label: string }> = [
  { key: "", label: "all" },
  { key: "completed", label: "completed" },
  { key: "failed", label: "failed" },
  { key: "pending_approval", label: "pending" },
  { key: "denied", label: "denied" },
];

const SINCE_RANGES: Array<{ label: string; ms: number }> = [
  { label: "1h", ms: 60 * 60 * 1000 },
  { label: "24h", ms: 24 * 60 * 60 * 1000 },
  { label: "7d", ms: 7 * 24 * 60 * 60 * 1000 },
  { label: "30d", ms: 30 * 24 * 60 * 60 * 1000 },
];

export function Activity() {
  const { engagementId } = useEngagement();
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agentFilter, setAgentFilter] = useState<string>("");
  const [actionFilter, setActionFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [sinceMs, setSinceMs] = useState<number>(SINCE_RANGES[2].ms);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      const since = Date.now() - sinceMs;
      const params = new URLSearchParams({
        limit: "100",
        since: String(since),
      });
      if (engagementId) params.set("engagement", engagementId);
      if (agentFilter) params.set("agent", agentFilter);
      if (actionFilter) params.set("action", actionFilter);
      if (statusFilter) params.set("status", statusFilter);
      fetch(`/api/activity?${params.toString()}`, { signal })
        .then((r) => (r.ok ? r.json() : { rows: [] }))
        .then((body) => {
          const typed = body as { rows?: ActivityRow[] };
          setRows(Array.isArray(typed.rows) ? typed.rows : []);
          setLoading(false);
        })
        .catch((err: Error) => {
          if (err.name === "AbortError") return;
          setError(err.message);
          setLoading(false);
        });
    },
    [engagementId, agentFilter, actionFilter, statusFilter, sinceMs],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const agents = useMemo(() => Array.from(new Set(rows.map((r) => r.agent))).sort(), [rows]);

  if (error) {
    return <ErrorBanner message={error} onRetry={load} context="activity" />;
  }

  return (
    <div className="space-y-6">
      <header className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="eyebrow">Console · 13</p>
          <h2 className="font-display italic text-3xl sm:text-4xl lg:text-[56px] leading-[0.95] mt-2 tracking-tight">
            <span className="text-[var(--color-text)]">Swarm </span>
            <span className="text-accent-gradient">Activity</span>
          </h2>
          <p className="mt-3 text-sm text-[var(--color-text-muted)] max-w-2xl">
            Timeline of every action recorded in <code>swarm_actions</code> — heartbeats, drafts, sends, verdicts, failures.
          </p>
        </div>

        <div className="flex gap-1" role="group" aria-label="Since range">
          {SINCE_RANGES.map((r) => {
            const active = r.ms === sinceMs;
            return (
              <button
                key={r.label}
                onClick={() => setSinceMs(r.ms)}
                className={`font-mono text-xs px-3 py-2 rounded transition-colors ${
                  active
                    ? "text-[var(--color-accent-glow)]"
                    : "text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                }`}
                style={{
                  background: active ? "var(--color-accent-soft)" : "var(--color-bg-card)",
                  border: `1px solid ${active ? "var(--color-accent)" : "var(--color-border)"}`,
                }}
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="panel p-4 sm:p-6">
        <div className="flex flex-wrap gap-2 mb-4 font-mono text-[10px]">
          <select
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded px-2 py-1 text-[var(--color-text)]"
            aria-label="Filter by agent"
          >
            <option value="">all agents</option>
            {agents.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <input
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            placeholder="action contains…"
            className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded px-2 py-1 text-[var(--color-text)] w-44"
            aria-label="Filter by action substring"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded px-2 py-1 text-[var(--color-text)]"
            aria-label="Filter by status"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          <span className="self-center text-[var(--color-text-dim)]">
            {loading ? "Loading…" : `${rows.length} rows`}
          </span>
        </div>

        {rows.length === 0 && !loading ? (
          <p className="text-center font-display italic text-2xl text-[var(--color-text-muted)] py-12">
            No activity in this window for these filters.
          </p>
        ) : (
          <ul className="divide-y divide-[var(--color-border)]">
            {rows.map((r) => {
              const expanded = expandedId === r.id;
              return (
                <li key={r.id} className="py-2">
                  <button
                    onClick={() => setExpandedId(expanded ? null : r.id)}
                    className="w-full flex items-center gap-3 text-left font-mono text-[11px]"
                    aria-expanded={expanded}
                  >
                    <span className="text-[var(--color-text-dim)] tabular-nums w-32 shrink-0">
                      {r.timestamp.slice(11, 19)} {r.timestamp.slice(0, 10)}
                    </span>
                    <span className="text-[var(--color-text)] w-20 shrink-0">{r.agent}</span>
                    <span className="text-[var(--color-text-muted)] flex-1">{r.action}</span>
                    <span
                      className={
                        r.status === "failed"
                          ? "text-[var(--color-status-error)]"
                          : r.status === "pending_approval"
                          ? "text-[var(--color-status-paused)]"
                          : "text-[var(--color-status-active)]"
                      }
                    >
                      {r.status}
                    </span>
                    <span className="text-[var(--color-text-dim)] w-32 shrink-0 text-right">{r.engagementId}</span>
                  </button>
                  {expanded && Object.keys(r.details).length > 0 && (
                    <pre className="mt-2 ml-32 text-[10px] font-mono text-[var(--color-text-muted)] bg-[var(--color-bg-card)] p-3 rounded overflow-x-auto">
                      {JSON.stringify(r.details, null, 2)}
                    </pre>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
